import { BenefitTypes } from '@strata/constants';
import { DateInputFormik } from '@strata/react-date-time';
import { FileUploadFormik } from '@strata/react-file-upload';
import { type FormAccordion } from '@strata/react-form-section';
import { PhoneInputFormik } from '@strata/react-inputs';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import * as Yup from 'yup';

import { IntakeSource } from 'services/constants';

export const FormSections = {
    POINT_OF_CONTACT: 1,
    HEALTH_PLAN_AND_PATIENT: 2,
    PRODUCT: 3,
    PRESCRIBER: 4,
    PATIENT: 5,
} as const;
export type FormSection = (typeof FormSections)[keyof typeof FormSections];

export const FormSectionTitleMap = {
    [FormSections.POINT_OF_CONTACT]: 'Point of Contact',
    // We're using the health_plan_and_patient section as the Intake Form's Patient section
    [FormSections.HEALTH_PLAN_AND_PATIENT]: 'Patient',
    [FormSections.PRODUCT]: 'Product',
    [FormSections.PRESCRIBER]: 'Prescriber',
    // The patient section is only used in the Case Details overview tab
    [FormSections.PATIENT]: 'Patient',
} as const;

export interface IntakeFormSectionStatus {
    isOpen: boolean;
    progressIndicatorStatus: (typeof FormAccordion.statuses)[keyof typeof FormAccordion.statuses];
}
export interface IntakeFormStatus {
    [FormSections.POINT_OF_CONTACT]: IntakeFormSectionStatus;
    [FormSections.HEALTH_PLAN_AND_PATIENT]: IntakeFormSectionStatus;
    [FormSections.PRODUCT]: IntakeFormSectionStatus;
    [FormSections.PRESCRIBER]: IntakeFormSectionStatus;
    [FormSections.PATIENT]: IntakeFormSectionStatus;
}

const memberIdSchema = Yup.string()
    .required(validationStrings.requiredField.errorMessage)
    .matches(/^\S+$/, 'Member ID cannot contain spaces.')
    .min(5, validationStrings.minCharacter.lambda(5))
    .max(15, validationStrings.maxCharacter.lambda(15));

/* Yup Schema for each section's captured data */
export const IntakeFormSectionSchemas = {
    [FormSections.POINT_OF_CONTACT]: Yup.object({
        intakeSource: Yup.string()
            .oneOf(Object.values(IntakeSource))
            .required(validationStrings.requiredField.errorMessage),
        contactFirstName: Yup.string().required(validationStrings.requiredField.errorMessage),
        contactLastName: Yup.string().required(validationStrings.requiredField.errorMessage),
        contactOfficeName: Yup.string().required(validationStrings.requiredField.errorMessage),
        contactPhoneNumber: PhoneInputFormik.validator().required(
            validationStrings.requiredField.errorMessage,
        ),
        contactFaxNumber: PhoneInputFormik.validator(),
        contactEmail: Yup.string().email(validationStrings.email.errorMessage),
        urgent: Yup.bool(),
        faxAttachment: FileUploadFormik.validator(),
        uploadedFaxAttachment: Yup.array(),
        intakeSourceId: Yup.string(),
    }),
    [FormSections.HEALTH_PLAN_AND_PATIENT]: Yup.object({
        payerId: Yup.string().required(validationStrings.requiredField.errorMessage),
        segmentId: Yup.string().required(validationStrings.requiredField.errorMessage),
        memberFirstName: Yup.string()
            .required(validationStrings.requiredField.errorMessage)
            .max(35, validationStrings.maxCharacter.lambda(35)),
        memberLastName: Yup.string()
            .required(validationStrings.requiredField.errorMessage)
            .max(35, validationStrings.maxCharacter.lambda(35)),
        memberDateOfBirth: DateInputFormik.validator()
            .required(validationStrings.requiredField.errorMessage)
            .max(dayjs().endOf('day'), 'Date of birth cannot be in the future.'),
        memberId: memberIdSchema,
        // Phone number in benefit check dialog
        benefitCheckPhone: PhoneInputFormik.validator(),
        // Fields for header
        memberVerified: Yup.bool(),
        memberEligible: Yup.bool(),
        memberPhoneNumber: PhoneInputFormik.validator(),
        memberCity: Yup.string().max(35, validationStrings.maxCharacter.lambda(35)),
        memberState: Yup.string(),
        memberZip: Yup.string().max(10, validationStrings.maxCharacter.lambda(10)),
    }),
    [FormSections.PRODUCT]: Yup.object({
        benefit: Yup.string()
            .oneOf(Object.values(BenefitTypes))
            .required(validationStrings.requiredField.errorMessage),
        placeOfService: Yup.string(),
        preferredPharmacyNpi: Yup.string(),
        productServiceOid: Yup.string()
            .required(validationStrings.requiredField.errorMessage)
            .max(105, validationStrings.maxCharacter.lambda(105)),
        productServiceId: Yup.string().required(validationStrings.requiredField.errorMessage),
        productServiceDescription: Yup.string().required(
            validationStrings.requiredField.errorMessage,
        ),
        isBrand: Yup.bool().required(validationStrings.requiredField.errorMessage),
        quantity: Yup.number()
            .positive(validationStrings.number.positiveNumbersOnly)
            .max(99999999999, 'Quantity must be less than 99999999999.'),
        strength: Yup.string(),
        productUnits: Yup.number().positive(validationStrings.number.positiveNumbersOnly),
        productUom: Yup.string(),
        daysSupply: Yup.number()
            .positive(validationStrings.number.positiveNumbersOnly)
            .max(999, 'Days supply must be less than 999.'),
        refills: Yup.number().positive(validationStrings.number.positiveNumbersOnly),
        rxSig: Yup.string(),
        diagnosisOid: Yup.string(),
        diagnosisCode: Yup.string(),
        needsByDate: DateInputFormik.validator().min(
            dayjs().startOf('day').toDate(),
            'Date cannot be in the past.',
        ),
        buyAndBill: Yup.bool(),
        sentToPharmacyNpi: Yup.string(),
        additionalMedications: Yup.array(Yup.string().required()),
        strengthUnitOfMeasure: Yup.string(),
        quantityUnitOfMeasure: Yup.string(),
    }),
    [FormSections.PRESCRIBER]: Yup.object().shape({
        prescriberNpi: Yup.string()
            .required(validationStrings.requiredField.errorMessage)
            .length(10, validationStrings.exactCharacter.lambda(10)),
        prescriberFirstName: Yup.string()
            .required(validationStrings.requiredField.errorMessage)
            .max(35, validationStrings.maxCharacter.lambda(35)),
        prescriberLastName: Yup.string()
            .required(validationStrings.requiredField.errorMessage)
            .max(35, validationStrings.maxCharacter.lambda(35)),
        prescriberVerified: Yup.bool(),
        prescriberPhoneNumber: PhoneInputFormik.validator().required(
            validationStrings.requiredField.errorMessage,
        ),
        prescriberFaxNumber: PhoneInputFormik.validator().required(
            validationStrings.requiredField.errorMessage,
        ),
        prescriberAddress1: Yup.string()
            .trim()
            .required(validationStrings.requiredField.errorMessage)
            .max(100, validationStrings.maxCharacter.lambda(100)),
        prescriberAddress2: Yup.string().trim().nullable(),
        prescriberCity: Yup.string()
            .trim()
            .required(validationStrings.requiredField.errorMessage)
            .max(35, validationStrings.maxCharacter.lambda(35)),
        prescriberState: Yup.string().trim().required(validationStrings.requiredField.errorMessage),
        prescriberZip: Yup.string()
            .trim()
            .length(5, validationStrings.exactCharacter.lambda(5))
            .required(validationStrings.requiredField.errorMessage),
        prescriberPracticeNpi: Yup.string()
            .length(10, validationStrings.exactCharacter.lambda(10))
            .nullable(),
        prescriberPracticeName: Yup.string().nullable(),
        prescriberPracticePhoneNumber: PhoneInputFormik.validator().nullable(),
        prescriberPracticeFaxNumber: PhoneInputFormik.validator().nullable(),
        prescriberPracticeAddress1: Yup.string().nullable(),
        prescriberPracticeAddress2: Yup.string().nullable(),
        prescriberPracticeCity: Yup.string().nullable(),
        prescriberPracticeState: Yup.string().nullable(),
        prescriberPracticeZip: Yup.string()
            .length(5, validationStrings.exactCharacter.lambda(5))
            .nullable(),
        prescriberPref: Yup.string(),
    }),
    [FormSections.PATIENT]: Yup.object({
        memberId: memberIdSchema,
        memberGender: Yup.string().required(validationStrings.requiredField.errorMessage),
        memberAddress1: Yup.string()
            .trim()
            .required(validationStrings.requiredField.errorMessage)
            .max(100, validationStrings.maxCharacter.lambda(100)),
        memberAddress2: Yup.string().trim().nullable(),
        memberCity: Yup.string()
            .trim()
            .required(validationStrings.requiredField.errorMessage)
            .max(35, validationStrings.maxCharacter.lambda(35)),
        memberState: Yup.string().trim().required(validationStrings.requiredField.errorMessage),
        memberZipcode: Yup.string().trim().required(validationStrings.requiredField.errorMessage),
        memberPhoneNumber: PhoneInputFormik.validator(),
        bin: Yup.string(),
        pcn: Yup.string(),
        rxGroup: Yup.string(),
    }),
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fullSchema = Yup.object(IntakeFormSectionSchemas);
/** Interface for full form data stored in redux */
export type IntakeFormSchema = Yup.InferType<typeof fullSchema>;

export const InitialIntakeFormValues: IntakeFormSchema = {
    [FormSections.POINT_OF_CONTACT]: {} as Yup.InferType<
        (typeof IntakeFormSectionSchemas)[typeof FormSections.POINT_OF_CONTACT]
    >,
    [FormSections.HEALTH_PLAN_AND_PATIENT]: {} as Yup.InferType<
        (typeof IntakeFormSectionSchemas)[typeof FormSections.HEALTH_PLAN_AND_PATIENT]
    >,
    [FormSections.PRODUCT]: {} as Yup.InferType<
        (typeof IntakeFormSectionSchemas)[typeof FormSections.PRODUCT]
    >,
    [FormSections.PRESCRIBER]: {} as Yup.InferType<
        (typeof IntakeFormSectionSchemas)[typeof FormSections.PRESCRIBER]
    >,
    [FormSections.PATIENT]: {} as Yup.InferType<
        (typeof IntakeFormSectionSchemas)[typeof FormSections.PATIENT]
    >,
};
